import { Link } from "react-router-dom";
import { ChevronDown, MessageCircle, Mail, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { Button, buttonVariants } from "@/components/flowly/infra/Button/Button";
import { useLanguage } from "@/contexts/LanguageContext";
import { SUPPORT_EMAIL } from "@/config/constants";
import { useHelp } from "./useHelp";

const Help = () => {
  const { t } = useLanguage();
  const {
    selectedCategory,
    setSelectedCategory,
    openItems,
    toggleItem,
    filteredFaqs,
    helpCategories,
  } = useHelp();

  return (
    <PageContainer className="py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">{t.help.title}</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t.help.subtitle}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {helpCategories.map(cat => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? "primary" : "secondary"}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
            >
              <cat.icon className="w-4 h-4" />
              {cat.label}
            </Button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="space-y-3 mb-16">
          {filteredFaqs.map((faq, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-xl overflow-hidden"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full flex items-center justify-between p-5 text-start hover:bg-muted/50 transition-colors"
              >
                <span className="font-semibold text-foreground">{faq.question}</span>
                <ChevronDown
                  className={cn(
                    "w-5 h-5 text-muted-foreground transition-transform",
                    openItems.includes(index) && "rotate-180"
                  )}
                />
              </button>
              {openItems.includes(index) && (
                <div className="px-5 pb-5 pt-0">
                  <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-3">{t.help.stillNeedHelp}</h2>
          <p className="text-muted-foreground mb-6">
            {t.help.teamHere}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className={cn(buttonVariants({ variant: "primary", size: "sm", rounded: "xl" }), "px-6 py-3 font-bold")}
            >
              <Mail className="w-5 h-5" />
              {t.help.sendMessageBtn}
            </Link>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className={cn(buttonVariants({ variant: "secondary", size: "sm", rounded: "xl" }), "px-6 py-3 font-bold")}
            >
              <MessageCircle className="w-5 h-5" />
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
    </PageContainer>
  );
};

export default Help;
